import { useQuery } from "react-query";
import LoanCard from "../../shared/LoanCard";
import {
  Circle,
  Loader,
  LucideUpload,
  Search,
  Sigma,
  SortAsc,
  UserPlus,
} from "lucide-react";
import { getAllGuarantors } from "../../sdk/guarantor/guarantor";
import { useState } from "react";
import Loading from "../../shared/Loading";
import { Button, Dropdown } from "antd";
import { useFormattedDate } from "../../hooks/useFormattedDate";
import Pagination from "../../shared/Pagination";
import { useNavigate } from "react-router-dom";
import FilterGuarantor from "../../components/filter/FilterGuarantor";
import MyChattels from "../../components/view-chattel/MyChattels";
import { SkeletonLoansPage } from "../../skeletons/LoansSkeletons";
import { useFormatAmount } from "../../hooks/useFormatAmount";
import decimalToDMS from "../../utils/location";

const Guarantors = () => {
  const [totalPages, setTotalPages] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [fromDate, setFromDate] = useState(
    new Date("2023-11-23").toISOString(),
  );
  const [toDate, setToDate] = useState(new Date().toISOString());
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [guarantors, setGuarantors] = useState([]);
  const [selectedGuarantors, setSelectedGuarantors] = useState([]);
  const formattedDate = useFormattedDate();
  const navigate = useNavigate();
  const [summary, setSummary] = useState({});
  const [openGuarantorFilter, setOpenGuarantorFilter] = useState(false);
  const [openMyChattels, setOpenMyChattels] = useState(false);
  const [selectedChattels, setSelectedChattels] = useState([]);
  const formatAmount = useFormatAmount();

  const toggleGuarantorFilter = () => {
    setOpenGuarantorFilter(true);
  };
  const closeGuarantorFilter = () => {
    setOpenGuarantorFilter(false);
  };

  // Function to handle individual checkbox toggle
  const handleCheckboxChange = (id) => {
    setSelectedGuarantors(
      (prev) =>
        prev.includes(id)
          ? prev.filter((guarantorId) => guarantorId !== id) // remove if already selected
          : [...prev, id], // add if not selected
    );
  };

  // Function to handle "select all"
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedGuarantors(guarantors.map((g) => g.id));
    } else {
      setSelectedGuarantors([]);
    }
  };

  const toggleMyChattels = (chattels) => {
    setSelectedChattels(chattels);
    setOpenMyChattels(true);
  };
  const closeMyChattels = () => {
    setOpenMyChattels(false);
  };

  const {
    refetch: refetchGuarantors,
    isRefetching,
    isLoading,
  } = useQuery({
    queryKey: [
      "all guarantor",
      page,
      limit,
      fromDate ? new Date(fromDate).toISOString() : null,
      toDate ? new Date(toDate).toISOString() : null,
      status,
    ],
    queryFn: async () => {
      const response = await getAllGuarantors(
        page,
        limit,
        fromDate ? new Date(fromDate).toISOString() : null,
        toDate ? new Date(toDate).toISOString() : null,
        search,
        status,
      );
      return response?.data?.data;
    },
    onSuccess: (data) => {
      setGuarantors(data?.rows);
      setPage(data?.page);
      setLimit(data?.limit);
      setTotalPages(data.totalPages);
      setTotalItems(data.total);
      setSummary(data?.summary);
    },
  });

  const items = [
    {
      key: "csv",
      label: "Export as CSV",
      onClick: () => {},
    },
    {
      key: "pdf",
      label: "Export as PDF",
      onClick: () => {},
    },
  ];

  const selectedItems = [
    {
      key: "csv",
      label: "Export as CSV",
      onClick: () => {},
    },
    {
      key: "pdf",
      label: "Export as PDF",
      onClick: () => {},
    },
  ];

  const handlePageChange = (page) => {
    setPage(page);
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, totalItems);

  return (
    <>
      {openMyChattels && (
        <MyChattels
          closeMyChattels={closeMyChattels}
          chattels={selectedChattels}
        />
      )}
      {openGuarantorFilter && (
        <FilterGuarantor
          closeFilters={closeGuarantorFilter}
          status={status}
          setStatus={setStatus}
          fromDate={fromDate}
          setFromDate={setFromDate}
          handleApplyFilter={closeGuarantorFilter}
          toDate={toDate}
          setToDate={setToDate}
        />
      )}
      {isLoading ? (
        <SkeletonLoansPage />
      ) : (
        <div className="w-full pl-[10px]">
          <div className="flex items-center h-[60px] mb-[10px] justify-between">
            <div className="flex items-center gap-[5px]">
              <span className="font-bold text-[20px] text-[#000000]">
                Loan Guarantors
              </span>
            </div>
          </div>
          <div className="flex items-center mb-[20px] gap-[20px]">
            <LoanCard
              icon={<Sigma />}
              label="Active Guarantors"
              value={summary?.guarantors?.active ?? 0}
              increment={`+${summary?.guarantors?.activeChangePct ?? 0.0}%`}
              padColor="#4B5D67"
            />
            <LoanCard
              icon={<Circle color="#F5A623" />}
              label="Inactive Guarantors"
              value={summary?.guarantors?.inactive ?? 0}
              increment={`+${summary?.guarantors?.inactiveChangePct ?? 0.0}%`}
              padColor="#F5A623"
            />
            <LoanCard
              icon={<Circle color="#00A676" />}
              label="Active Guarantees"
              value={formatAmount(summary?.guarantees?.active ?? 0)}
              increment={`+${summary?.guarantees?.activeChangePct ?? 0.0}%`}
              padColor="#00A676"
            />
            <LoanCard
              icon={<Circle color="grey" />}
              label="Inactive Guarantees"
              value={formatAmount(summary?.guarantees?.inactive ?? 0)}
              increment={`+${summary?.guarantees?.inactiveChangePct ?? 0.0}%`}
              padColor="#808080"
            />
          </div>
          <div className="h-[70px] w-full bg-white flex items-center justify-between px-[10px]">
            <button
              onClick={refetchGuarantors}
              className="h-[35px] w-[120px] flex items-center justify-center gap-[5px] border border-[#00A676] bg-white rounded-[10px] text-[#00A676] font-medium text-[15px]"
            >
              {isRefetching ? <Loading /> : <Loader size={17} />}
              Update
            </button>
            <div className="h-[40px] w-[117px] text-[#5C5C5C] font-medium text-[15px] rounded-[10px] border border-[#F3F3F3] bg-white flex items-center justify-center">
              0 Selected
            </div>
            <div
              onClick={toggleGuarantorFilter}
              className="h-[40px] w-[127px] cursor-pointer text-[#5C5C5C] font-medium text-[15px] rounded-[10px] border border-[#F3F3F3] bg-[#FBF5F5] flex items-center justify-center gap-[5px]"
            >
              <SortAsc color="#000000" />
              <span className="text-[#000000]">Filter</span>
            </div>
            <div className="flex items-center border border-gray-300 rounded-md px-2 h-[40px] w-[50%] bg-white">
              <Search className="text-gray-500 w-5 h-5 mr-2" />
              <input
                type="text"
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search guarantors..."
                className="w-full outline-none text-sm text-gray-700 placeholder-gray-400 bg-transparent"
              />
              <button
                onClick={refetchGuarantors}
                className="ml-2 p-2 bg-blue-600 rounded-md hover:bg-blue-700"
              >
                <Search className="w-4 h-4 text-white" />
              </button>
            </div>
            <p className="text-[#000000] truncate text-[15px] font-normal">
              {guarantors?.length} Results
            </p>
            {selectedGuarantors?.length === 0 ? (
              <Dropdown menu={{ items }} trigger={["click"]} placement="bottom">
                <Button className="flex items-center bg-[#F3F3F3] border-[0.7px] border-[#A8A8A8] h-[40px] rounded-[30px] px-[20px] justify-center gap-[5px] text-[16px] text-[#5C5C5C]">
                  <LucideUpload size={17} />
                  <span>Export</span>
                </Button>
              </Dropdown>
            ) : (
              <Dropdown
                menu={{ items: selectedItems }}
                trigger={["click"]}
                placement="bottom"
              >
                <Button className="flex items-center bg-[#F3F3F3] border-[0.7px] border-[#A8A8A8] h-[40px] rounded-[30px] px-[20px] justify-center gap-[5px] text-[16px] text-[#5C5C5C]">
                  <LucideUpload size={17} />
                  <span>Export</span>
                </Button>
              </Dropdown>
            )}
          </div>
          <div>
            <div className="h-[44px] px-[10px] w-full bg-[#F3F3F3] mb-[10px] flex items-center">
              <div className="w-[3%]">
                <input
                  type="checkbox"
                  checked={selectedGuarantors.length === guarantors?.length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </div>
              <div className="w-[6%] text-[#000] font-bold text-[13px]">ID</div>
              <div className="w-[12%] text-[#000] truncate font-bold text-[13px]">
                GUARANTOR NAME
              </div>
              <div className="w-[10%] text-[#000] font-bold text-[13px] truncate">
                PHONE NUMBER
              </div>
              <div className="w-[10%] text-[#000] font-bold text-[13px] truncate">
                ID NUMBER
              </div>
              <div className="w-[14%] text-[#000] font-bold text-[13px] truncate">
                EMAIL
              </div>
              <div className="w-[8%] text-[#000] truncate font-bold text-[13px]">
                RESIDENCE
              </div>
              <div className="w-[14%] text-[#000] font-bold text-[13px] truncate">
                EXACT LOCATION
              </div>
              <div className="w-[10%] text-[#000] font-bold text-[13px] truncate">
                DATE CREATED
              </div>
              <div className="w-[10%] text-[#000] font-bold text-[13px] truncate">
                STATUS
              </div>
              <div className="w-[3%] text-[#000] font-bold text-[13px] truncate">
                CHATTELS
              </div>
            </div>
            {guarantors?.length > 0 ? (
              guarantors?.map((guarantor) => (
                <div className="h-[40px] px-[10px] w-full bg-[#FFFFFF] mb-[7px] flex items-center">
                  <div className="w-[3%]">
                    <input
                      type="checkbox"
                      checked={selectedGuarantors.includes(guarantor.id)}
                      onChange={() => handleCheckboxChange(guarantor.id)}
                    />
                  </div>
                  <div
                    onClick={() =>
                      navigate(`/dashboard/guarantors/${guarantor?.id}`)
                    }
                    className="w-[6%] cursor-pointer truncate text-[#2A8C52] font-normal text-[14px]"
                  >
                    {guarantor.public_id}
                  </div>
                  <div className="w-[12%] text-[#000] truncate font-normal text-[14px]">
                    {guarantor.guarantorName}
                  </div>
                  <div className="w-[10%] text-[#000] font-normal text-[14px]">
                    {guarantor.guarantorPhone || "N/A"}
                  </div>
                  <div className="w-[10%] text-[#000] font-normal text-[14px]">
                    {guarantor.guarantorIDNumber || "N/A"}
                  </div>
                  <div className="w-[14%] truncate text-[#000] font-normal text-[14px]">
                    {guarantor.guarantorEmail || "N/A"}
                  </div>
                  <div className="w-[8%] text-[#000] truncate font-normal text-[14px]">
                    {guarantor.residenceLocation ?? "N/A"}
                  </div>
                  <div className="w-[14%] text-[#000] truncate font-normal text-[14px]">
                    {`${decimalToDMS(
                      guarantor?.residenceLatitude,
                      true,
                    )} ${decimalToDMS(guarantor?.residenceLongitude, false)}`}
                  </div>
                  <div className="w-[10%] text-[#000] font-normal text-[14px]">
                    {formattedDate(guarantor?.createdAt)}
                  </div>
                  <div className="w-[10%] text-[#000] capitalize font-normal text-[14px]">
                    {guarantor?.status}
                  </div>
                  <div
                    onClick={() => toggleMyChattels(guarantor?.chattels)}
                    className="w-[3%] cursor-pointer text-[#00ff] text-[#000] font-medium text-[14px]"
                  >
                    {guarantor?.chattels?.length ?? 0}
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center py-24 px-6 text-center bg-white">
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-green-100 blur-2xl rounded-full opacity-50" />
                  <div className="relative w-20 h-20 bg-green-50 rounded-3xl flex items-center justify-center border border-green-100 shadow-sm">
                    <UserPlus size={32} className="text-[#2A8C52]" />
                  </div>
                </div>

                <h3 className="text-gray-900 font-bold text-xl tracking-tight">
                  No Guarantors Found
                </h3>
                <p className="text-gray-500 max-w-[320px] mt-2 text-sm leading-relaxed">
                  You haven't added any guarantors to this record. Start by
                  adding a new contact to verify this application.
                </p>

                <button
                  className="mt-8 px-6 py-2.5 bg-[#000000] text-white text-sm font-semibold rounded-xl hover:bg-[#2A8C52] hover:shadow-lg hover:shadow-green-900/20 transition-all flex items-center gap-2"
                  onClick={() => {}}
                >
                  <UserPlus size={18} />
                  Add New Guarantor
                </button>
              </div>
            )}
            <div className="h-[50px] mb-[20px] flex items-center justify-between">
              <div className="flex items-center gap-[10px]">
                <p className="text-[15px] text-[#5C5C5C] font-normal">
                  {start}–{end} of {totalItems}
                </p>
                <p className="text-[15px] text-[#5C5C5C] font-normal">
                  Row/Page:
                </p>
                <p className="text-[15px] text-[#5C5C5C] font-normal">
                  {page}/{totalPages}
                </p>
                <select
                  className="h-[35px] w-[100px] px-[10px] text-[12px]"
                  name="row"
                  id="row"
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                >
                  <option value="10">10 items</option>
                  <option value="20">20 items</option>
                  <option value="25">25 items</option>
                </select>
              </div>
              <div>
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Guarantors;
